import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { setBirthDate } from "../../api/auth";
import { postDecisions, type ConsentOverview, type TopicKey } from "../../api/consent";
import { useAccountConsent } from "../../hooks/useAccountConsent";
import { apiErrorCode, apiStatus, isUnderAgeError, safeError } from "../../lib/apiError";
import { gateDecisions, gateView, rememberDeviceAnswers } from "../../lib/consentSession";
import { applyDeviceConsent } from "../../lib/deviceConsent";
import logger from "../../lib/logger";
import { logout } from "../../lib/logout";
import { useAuthStore } from "../../store/authStore";
import { useConsentStore } from "../../store/consentStore";
import BeforeYouStart from "./BeforeYouStart";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "./ConsentDialog";
import DateOfBirthDialog from "./DateOfBirthDialog";
import ReconsentModal from "./ReconsentModal";

/** The page behind a gate dialog: nothing of the app shows (and loads) until the account has answered. */
function GatePage({ children }: { children?: React.ReactNode }) {
  return (
    <div
      data-testid="consent-gate"
      className="min-h-full flex items-center justify-center"
      style={{ background: "linear-gradient(135deg, var(--muted) 0%, var(--background) 60%)" }}
    >
      {children}
    </div>
  );
}

/**
 * The blocking consent gate of a signed-in account (Amendment C order): Date of birth first while `birthDatePending`
 * (PUT /profile/birth-date), then Before you start (existing user) or We updated our terms while a gate topic needs an
 * answer. Wraps the protected layout and the profile setup page; renders `children` once nothing is pending.
 * Under 14 → the account is signed out by the server; we drop it and show Under 14 on the sign-in page.
 */
export default function ConsentGate({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const signedIn = useAuthStore((s) => s.isAuthenticated());
  const userId = useAuthStore((s) => s.userId);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const consentPending = useConsentStore((s) => s.consentPending);
  const birthDatePending = useConsentStore((s) => s.birthDatePending);
  const setPending = useConsentStore((s) => s.setPending);
  const setBirthDatePending = useConsentStore((s) => s.setBirthDatePending);
  const [dobBusy, setDobBusy] = useState(false);
  const [dobError, setDobError] = useState<string | null>(null);

  const consent = useAccountConsent();
  const data = consent.data;

  // The server's answer is the truth for the gate flag (it may have been answered on another device).
  useEffect(() => {
    if (data) setPending(data.consentPending);
  }, [data, setPending]);

  if (!signedIn) return <>{children}</>;

  async function signOutAndLeave(state?: { under14: true }) {
    await logout(userId ?? "", clearAuth);
    navigate("/login", { replace: true, state });
  }

  async function submitBirthDate(birthDate: string) {
    setDobBusy(true);
    setDobError(null);
    try {
      await setBirthDate(birthDate);
      setBirthDatePending(false);
    } catch (e) {
      if (isUnderAgeError(e)) {
        // The server stored the date, deactivated the account and ended its sessions.
        await signOutAndLeave({ under14: true });
        return;
      }
      if (apiErrorCode(e) === "BIRTH_DATE_SET") {
        setBirthDatePending(false);
        return;
      }
      const status = apiStatus(e);
      logger.warn("[Consent] date of birth not saved", status);
      setDobError(
        status === undefined || status === 503
          ? t("auth_try_again")
          : status === 400
            ? t("dob_invalid")
            : t("consent_error"),
      );
    } finally {
      setDobBusy(false);
    }
  }

  if (birthDatePending) {
    const privacy = data?.topics.find((x) => x.key === "privacy_notice");
    return (
      <GatePage>
        <DateOfBirthDialog
          busy={dobBusy}
          error={dobError}
          privacyUrl={privacy?.url}
          onContinue={(birthDate) => void submitBirthDate(birthDate)}
          onSignOut={() => void signOutAndLeave()}
        />
      </GatePage>
    );
  }

  const view = gateView(data);
  if (!data) {
    if (!consentPending) return <>{children}</>;
    if (consent.isError) {
      return (
        <GatePage>
          <div className="flex flex-col items-center gap-3 px-4 text-center">
            <p data-testid="consent-gate-error" className="text-sm text-[var(--destructive-text)]">
              {t("auth_try_again")}
            </p>
            <div className="flex gap-2">
              <button type="button" className={PRIMARY_BUTTON} onClick={() => void consent.refetch()}>
                {t("consent_retry")}
              </button>
              <button type="button" className={SECONDARY_BUTTON} onClick={() => void signOutAndLeave()}>
                {t("logout_label")}
              </button>
            </div>
          </div>
        </GatePage>
      );
    }
    return <GatePage />;
  }
  if (view.kind === "none") {
    return (
      <>
        {children}
        {/* The account's overview has arrived and asks nothing: e2e waits for this before calling the app usable
            (a persisted consentPending=false draws the app before the overview says otherwise). Dev builds only. */}
        <span hidden data-testid="consent-ready" />
      </>
    );
  }

  const optional = view.kind === "reconsent" ? view.changed : view.optional;

  async function confirm(ticked: Set<TopicKey>) {
    const out = await postDecisions(
      gateDecisions(view.kind === "none" ? [] : view.required, optional, ticked, "RECONSENT", "FIRST_START"),
    );
    rememberDeviceAnswers(optional, ticked);
    applyDeviceConsent();
    setPending(out.consentPending);
    queryClient.setQueryData<ConsentOverview>(consent.key, (old) =>
      old ? { ...old, consentPending: out.consentPending } : old,
    );
    void queryClient.invalidateQueries({ queryKey: ["consent"] });
  }

  async function decline() {
    const terms = view.kind === "none" ? undefined : view.required.find((x) => x.key === "terms");
    if (terms) {
      try {
        await postDecisions(gateDecisions([terms], [], new Set(), "RECONSENT", "RECONSENT"));
      } catch (e) {
        logger.warn("[Consent] recording the declined terms failed", safeError(e));
      }
    }
    await signOutAndLeave();
  }

  return (
    <GatePage>
      {view.kind === "reconsent" ? (
        <ReconsentModal required={view.required} changed={view.changed} onConfirm={confirm} onDecline={decline} />
      ) : (
        <BeforeYouStart
          mode="existing"
          required={view.required}
          optional={view.optional}
          onConfirm={confirm}
          onDecline={decline}
        />
      )}
    </GatePage>
  );
}

